/**
 * Settings page access control utility.
 *
 * All Settings categories (Audit, Federation, IAM) require admin access.
 * The backend enforces this on every endpoint. The frontend mirrors it
 * as a UX convenience layer.
 *
 * The ui_permissions from scopes.yml control server/agent access
 * (e.g., list_service, toggle_service, list_agents, publish_agent)
 */

interface SettingsUser {
  is_admin?: boolean;
}

/**
 * Check if a user can access the Settings page.
 * Returns true only when is_admin === true.
 */
export function canAccessSettings(user: SettingsUser | null): boolean {
  if (!user) return false;
  return user.is_admin === true;
}

interface VirtualServerUser {
  is_admin?: boolean;
  can_modify_servers?: boolean;
  groups?: string[];
  scopes?: string[];
  accessible_servers?: string[];
}

/**
 * Check if a user can create a virtual server (mirrors the backend gate).
 */
export function canCreateVirtualServer(user: VirtualServerUser | null): boolean {
  if (!user) return false;
  return isRegistryAdmin(user) || user.can_modify_servers === true;
}

/**
 * Check if a user can edit, toggle or delete a virtual server.
 *
 * Mirrors registry/api/virtual_server_routes.py: admins always can; other users
 * need server-modify permission and access to every backend server.
 */
export function canModifyVirtualServer(
  user: VirtualServerUser | null,
  backendPaths: string[] | undefined,
): boolean {
  if (!user) return false;
  if (isRegistryAdmin(user)) return true;
  if (user.can_modify_servers !== true) return false;
  const accessible = (user.accessible_servers || []).map((s) => s.replace(/^\/+|\/+$/g, ''));
  if (accessible.includes('all') || accessible.includes('*')) return true;
  return (backendPaths || []).every((path) =>
    accessible.includes(path.replace(/^\/+|\/+$/g, '')),
  );
}

function isRegistryAdmin(user: VirtualServerUser): boolean {
  return (
    user.is_admin === true ||
    (user.groups || []).includes('mcp-registry-admin') ||
    (user.scopes || []).includes('mcp-registry-admin')
  );
}
